/**
 * Recent Transactions Component
 * Displays the last 5 transactions with quick actions
 */
import { Link } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { Transaction } from '@/types';

interface RecentTransactionsProps {
  transactions: Transaction[];
  isLoading?: boolean;
  hasConnections: boolean;
}

function TransactionIcon({ type }: { type: Transaction['type'] | undefined }) {
  if (type === 'income') {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
        <svg
          className="h-5 w-5 text-flow-income"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m0-16l-4 4m4-4l4 4"
          />
        </svg>
      </div>
    );
  }

  if (type === 'transfer') {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
        <svg
          className="h-5 w-5 text-flow-transfer"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
      <svg
        className="h-5 w-5 text-flow-expense"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 20V4m0 16l4-4m-4 4l-4-4"
        />
      </svg>
    </div>
  );
}

function TransactionSkeleton() {
  return (
    <div className="flex items-center gap-4 py-3">
      <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
      <div className="flex-1">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
        <div className="mt-1 h-3 w-20 animate-pulse rounded bg-gray-100" />
      </div>
      <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
    </div>
  );
}

function EmptyState({ hasConnections }: { hasConnections: boolean }) {
  if (!hasConnections) {
    return (
      <div className="py-12 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No bank connected</h3>
        <p className="mt-1 text-sm text-gray-500">
          Connect your bank account to see your transactions.
        </p>
        <div className="mt-4">
          <Link to="/connections">
            <Button variant="primary" size="sm">
              Connect Bank
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 text-center">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions yet</h3>
      <p className="mt-1 text-sm text-gray-500">
        Your transactions will appear here after syncing with your bank.
      </p>
    </div>
  );
}

export default function RecentTransactions({
  transactions,
  isLoading,
  hasConnections,
}: RecentTransactionsProps) {
  return (
    <Card className="md:col-span-2 lg:col-span-2">
      <CardHeader
        title="Recent Transactions"
        description="Your latest financial activity"
        action={
          transactions.length > 0 ? (
            <Link to="/transactions" className="text-sm font-medium text-primary-600 hover:text-primary-700">
              View all
            </Link>
          ) : null
        }
      />

      {isLoading ? (
        <div className="divide-y divide-gray-100">
          {[1, 2, 3, 4, 5].map((i) => (
            <TransactionSkeleton key={i} />
          ))}
        </div>
      ) : transactions.length === 0 ? (
        <EmptyState hasConnections={hasConnections} />
      ) : (
        <div className="divide-y divide-gray-100">
          {transactions.map((transaction) => {
            // Derive type from amount if not explicitly set
            const txnType = transaction.type ?? (transaction.amount > 0 ? 'income' : 'expense');
            const displayName = transaction.merchantName || transaction.name || 'Unknown';
            const displayDate = transaction.date ? formatDate(transaction.date, { month: 'short', day: 'numeric' }) : '';
            const displayAmount = Math.abs(Number(transaction.amount) || 0);

            return (
              <div key={transaction.id} className="flex items-center gap-4 py-3">
                <TransactionIcon type={txnType} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {displayName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {displayDate}
                    {transaction.pending && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        Pending
                      </span>
                    )}
                  </p>
                </div>
                <div
                  className={cn(
                    'text-sm font-semibold',
                    txnType === 'income'
                      ? 'text-flow-income'
                      : txnType === 'transfer'
                      ? 'text-flow-transfer'
                      : 'text-flow-expense'
                  )}
                >
                  {txnType === 'income' ? '+' : txnType === 'expense' ? '-' : ''}
                  {formatCurrency(displayAmount)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
